import * as React from 'react';
import { css, ThemeContext } from 'styled-components';
import { Transition } from 'react-transition-group';
import FocusLock from 'react-focus-lock';

import { ScreenReaderOnly } from '../../Accessibility';
import SVGIcon from '../../Icon/SVGIcon';
import Box from '../Layout/Box';
import Text, { ITextProps } from '../Typography/Text/Text';
import Header from '../Typography/Headings/Heading';
import { VALID_TRANSITION_STATES } from '../Animations/Animations';
import { VALID_DIMENSION_DATA } from '../Utils/Styles/calcDimensionStyles';
import { isOutsideClick } from '../Utils/events/events';
import { useOnEscape } from '../Utils/Hooks/events/useOnEscape';

import { CloseModalButton, ModalOverlay, TRANSITION_DURATION } from './ModalStyles';

interface IModalProps {
  cardWidth: VALID_DIMENSION_DATA;
  ariaModal: boolean;
  ariaLabelledby: string;
  heading: string | { text: string; styles: ITextProps };
  isShown: boolean;
  hideCloseIcon?: boolean;
  handleModalClose (e: Event | MouseEvent | KeyboardEvent | React.SyntheticEvent): void;
}

export const Modal: React.FC<IModalProps> = ({
  ariaLabelledby,
  ariaModal,
  cardWidth,
  children,
  hideCloseIcon,
  handleModalClose,
  heading,
  isShown,
  ...rest
}) => {
  const { themeTwo: { heading: headingTheme , modal } } = React.useContext(ThemeContext);
  const modalRef = React.useRef<HTMLDivElement>(null);

  useOnEscape(e => {
    e.stopPropagation();
    isShown && handleModalClose(e);
  });

  // close modal when clicks outside card (on overlay)
  const handleOverlayClick: React.MouseEventHandler<HTMLDivElement> = e => {
      e.stopPropagation();
      const isOutsideContentARea = isOutsideClick(modalRef, e);
      if (isOutsideContentARea) { handleModalClose(e); }
  };

  return (
    <Transition
      in={isShown}
      timeout={TRANSITION_DURATION}
    >
      {(state: VALID_TRANSITION_STATES) => (
        state === VALID_TRANSITION_STATES.exited ? null : (
          <ModalOverlay
            {...rest}
            aria-modal={ariaModal}
            aria-labelledby={ariaLabelledby ? ariaLabelledby : undefined}
            role='dialog'
            transitionState={state}
            onClick={handleOverlayClick}
          >
            <FocusLock returnFocus={true} className='center-modal' autoFocus={false}>
              <Box
                backgroundColor={modal.card.backgroundColor}
                border={modal.card.border}
                direction='column'
                boxShadow={modal.card.shadow}
                margin={{ horizontal: 'auto' }}
                padding={modal.card.padding}
                position='relative'
                ref={modalRef}
                borderRadius={modal.card.round}
                width={cardWidth}
              >

                {typeof heading === 'object' && heading.styles ?
                  <Text
                    id={ariaLabelledby}
                    family={headingTheme.default.family}
                    {...heading.styles}
                    forwardedAs='h1'
                  >
                    {heading.text}
                  </Text>
                  :
                  <Header.h1
                    id={ariaLabelledby}
                  >
                    {heading}
                  </Header.h1>
                }

                {children}

                {!hideCloseIcon &&
                  <CloseModalButton onClick={handleModalClose}>
                    <ScreenReaderOnly>Close Modal</ScreenReaderOnly>
                    <SVGIcon
                      color={modal.closeIcon.color}
                      icon={modal.closeIcon.type}
                      size={modal.closeIcon.size}
                    />
                  </CloseModalButton>
                }
              </Box>
            </FocusLock>
          </ModalOverlay>
        )
      )}
    </Transition>
  );
};

export default Modal;
